import { call, put, select, takeLatest } from 'redux-saga/effects'
import {
  ClientProgramsMap,
  FetchClientProgramsPayload,
  ProgramsCorrespondingToTenant,
} from '../types'
import { PayloadAction } from '@reduxjs/toolkit'
import { SagaIterator } from 'redux-saga'
import {
  addToaster,
  fetchAllClientPrograms,
  hideLoader,
  showLoader,
  updateAllClientPrograms,
  updateAuthUserSelectedTenant,
  updateSelectedClientProgram,
} from '../slices'
import { getAllClientProgramsCorrespondingToUser } from '../apis'
import { v4 as uuid } from 'uuid'
import { DASHBOARD_CONSTANTS } from '../constants'

const transformToClientProgramsMap = (
  data: {
    id: number
    name_of_organization: string
    programs: ProgramsCorrespondingToTenant[]
  }[]
): ClientProgramsMap => {
  return data.reduce<ClientProgramsMap>((acc, tenant) => {
    acc[tenant.id.toString()] = {
      name_of_organization: tenant.name_of_organization,
      programs: tenant.programs,
    }
    return acc
  }, {})
}

const getDefaultProgram = (
  programs: ProgramsCorrespondingToTenant[]
): ProgramsCorrespondingToTenant =>
  programs.find((program: ProgramsCorrespondingToTenant) =>
    program.operational_program_name?.includes(DASHBOARD_CONSTANTS.BIOSIMILARS)
  ) ?? programs[0]

function* fetchAllClientProgramsFunction(
  action: PayloadAction<FetchClientProgramsPayload>
): SagaIterator {
  try {
    yield put(showLoader())
    const response = yield call(
      getAllClientProgramsCorrespondingToUser,
      action?.payload?.userId
    )
    yield put(
      updateAllClientPrograms(transformToClientProgramsMap(response.data))
    )
    let selectedTenant = yield select(
      (state) => state.authUser.selectedClientTenant
    )
    const allClientPrograms = yield select(
      (state) => state.clientPrograms?.allClientPrograms
    )
    const prevSelectedClientProgram = yield select(
      (state) => state?.clientPrograms?.selectedClientPrograms
    )

    if (!allClientPrograms) return
    if (parseInt(selectedTenant?.id) === -1) {
      const newTenant = Object.keys(allClientPrograms)[0]
      yield put(updateAuthUserSelectedTenant(newTenant))
      selectedTenant = yield select(
        (state) => state.authUser.selectedClientTenant
      )
    }

    if (allClientPrograms[selectedTenant?.id]) {
      if (prevSelectedClientProgram?.tenant_id === selectedTenant?.id) {
        yield put(
          updateSelectedClientProgram({
            tenant_id: selectedTenant?.id,
            program_id: prevSelectedClientProgram?.program_id,
            operational_program_name:
              prevSelectedClientProgram?.operational_program_name,
            name_of_organization: selectedTenant?.name_of_organization,
            isOverseer: prevSelectedClientProgram?.isOverseer,
          })
        )
      } else {
        const programs = allClientPrograms[selectedTenant?.id]?.programs ?? []
        const defaultProgram = getDefaultProgram(programs)
        yield put(
          updateSelectedClientProgram({
            tenant_id: selectedTenant?.id,
            program_id: defaultProgram?.id,
            operational_program_name: defaultProgram?.operational_program_name,
            name_of_organization: selectedTenant?.name_of_organization,
            isOverseer: defaultProgram?.permission_set_name.includes(
              DASHBOARD_CONSTANTS.OVERSEEING
            ),
          })
        )
      }
    } else if (
      prevSelectedClientProgram &&
      allClientPrograms[prevSelectedClientProgram?.tenant_id]
    ) {
      yield put(
        updateAuthUserSelectedTenant(prevSelectedClientProgram?.tenant_id)
      )
      selectedTenant = yield select(
        (state) => state.authUser.selectedClientTenant
      )
      const programs = allClientPrograms[selectedTenant?.id]?.programs ?? []
      const defaultProgram = getDefaultProgram(programs)
      yield put(
        updateSelectedClientProgram({
          tenant_id: selectedTenant?.id,
          program_id:
            prevSelectedClientProgram?.program_id ?? defaultProgram?.id,
          operational_program_name:
            prevSelectedClientProgram?.operational_program_name ??
            defaultProgram?.operational_program_name,
          name_of_organization: selectedTenant?.name_of_organization,
          isOverseer:
            prevSelectedClientProgram?.isOverseer ??
            defaultProgram?.permission_set_name.includes(
              DASHBOARD_CONSTANTS.OVERSEEING
            ),
        })
      )
    } else {
      if (!allClientPrograms[selectedTenant?.id]) {
        const newTenant = Object.keys(allClientPrograms)[0]
        yield put(updateAuthUserSelectedTenant(newTenant))
        selectedTenant = yield select(
          (state) => state.authUser.selectedClientTenant
        )
      }
      yield put(
        addToaster({
          id: uuid(),
          message: DASHBOARD_CONSTANTS.NO_PROGRAM_CONFIGURED,
          type: 'error',
        })
      )

      const programs = allClientPrograms[selectedTenant?.id]?.programs ?? []
      const defaultProgram = getDefaultProgram(programs)
      yield put(
        updateSelectedClientProgram({
          tenant_id: selectedTenant?.id,
          program_id: defaultProgram?.id,
          operational_program_name: defaultProgram?.operational_program_name,
          name_of_organization: selectedTenant?.name_of_organization,
          isOverseer: defaultProgram?.permission_set_name.includes(
            DASHBOARD_CONSTANTS.OVERSEEING
          ),
        })
      )
    }
  } catch (err) {
    console.log(err)
  } finally {
    yield put(hideLoader())
  }
}

function* syncProgramOnTenantChange(): SagaIterator {
  // const tenantId = String(action.payload)
  const allClientPrograms = yield select(
    (state) => state.clientPrograms?.allClientPrograms
  )
  let selectedTenant = yield select(
    (state) => state.authUser.selectedClientTenant
  )
  const prevSelectedClientProgram = yield select(
    (state) => state.clientPrograms?.selectedClientPrograms
  )
  if (!allClientPrograms[selectedTenant?.id]) {
    const newTenant = Object.keys(allClientPrograms)[0]
    yield put(updateAuthUserSelectedTenant(newTenant))
    selectedTenant = yield select(
      (state) => state.authUser.selectedClientTenant
    )
    if (!selectedTenant) return
  }
  if (prevSelectedClientProgram?.tenant_id === selectedTenant?.id) return
  const programs = allClientPrograms[selectedTenant?.id]?.programs ?? []
  const defaultProgram = getDefaultProgram(programs)
  yield put(
    updateSelectedClientProgram({
      tenant_id: selectedTenant?.id,
      program_id: defaultProgram?.id,
      operational_program_name: defaultProgram?.operational_program_name,
      name_of_organization: selectedTenant?.name_of_organization,
      isOverseer: defaultProgram?.permission_set_name?.includes(
        DASHBOARD_CONSTANTS.OVERSEEING
      ),
    })
  )
}

export function* watchFetchAllClientPrograms() {
  yield takeLatest(fetchAllClientPrograms.type, fetchAllClientProgramsFunction)
}

export function* watchSyncProgramOnTenantChange() {
  yield takeLatest(updateAuthUserSelectedTenant.type, syncProgramOnTenantChange)
}
